import { useClipboard } from '@/shared/hooks/useClipboard';
import { formatDateTime } from '@/shared/lib/format';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { CommandInput, CommandItem, CommandList } from '@/shared/ui/command';
import { router } from '@inertiajs/react';
import { ArrowLeft } from 'lucide-react';
import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import { toast } from 'sonner';
import { keysToCsv } from '../lib/keysCsv';
import { Hint } from '../PaletteHint';
import type { PaletteViewProps } from './registry';
import { useKeysByWifi, type WifiKey } from './useKeysByWifi';

function Message({ children }: { children: string }) {
    return <div className="py-8 text-center text-sm text-muted-foreground">{children}</div>;
}

export function KeysByWifiView({ close, back, initialQuery }: PaletteViewProps) {
    const [input, setInput] = useState(initialQuery);
    const { wifi, status, items, truncated } = useKeysByWifi(input);
    // Selection is tied to the result set it was made on, so a new search starts empty.
    const [selection, setSelection] = useState<{ items: WifiKey[]; ids: Set<number> }>({ items, ids: new Set() });
    const selectedIds = selection.items === items ? selection.ids : new Set<number>();
    const { copy } = useClipboard();

    const allSelected = items.length > 0 && selectedIds.size === items.length;

    const setSelectedIds = (ids: Set<number>) => setSelection({ items, ids });

    const toggle = (id: number) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedIds(next);
    };

    const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(items.map((k) => k.id)));

    const copySelected = async () => {
        const rows = items.filter((k) => selectedIds.has(k.id));
        if (rows.length === 0) return;
        if (await copy(keysToCsv(rows))) toast.success(`Скопійовано ${rows.length} ключів`);
        else toast.error('Не вдалося скопіювати');
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            // preventDefault stops cmdk from also toggling the highlighted row.
            e.preventDefault();
            void copySelected();
        } else if (e.key === 'Backspace' && input === '') {
            e.preventDefault();
            back();
        }
    };

    const openOwner = (e: MouseEvent<HTMLAnchorElement>, url: string) => {
        e.stopPropagation();
        // Let the browser handle new-tab / new-window clicks.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        close();
        router.visit(url);
    };

    return (
        <>
            <div className="flex items-center border-b">
                <button
                    type="button"
                    aria-label="Назад"
                    onClick={back}
                    className="ml-2 flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                    <ArrowLeft className="size-4" />
                </button>
                <div className="flex-1 [&_[data-slot=command-input-wrapper]]:border-b-0">
                    <CommandInput value={input} onValueChange={setInput} onKeyDown={handleKeyDown} placeholder="*12345" autoFocus />
                </div>
            </div>

            {status === 'ready' && items.length > 0 && (
                <div className="flex items-center gap-3 border-b px-4 py-2 text-xs text-muted-foreground">
                    <span>
                        Знайдено {items.length} · вибрано {selectedIds.size}
                    </span>
                    <button type="button" onClick={toggleAll} className="hover:text-foreground">
                        {allSelected ? 'Зняти всі' : 'Вибрати всі'}
                    </button>
                    <Button size="sm" className="ml-auto h-7" disabled={selectedIds.size === 0} onClick={() => void copySelected()}>
                        Копіювати CSV ({selectedIds.size})
                    </Button>
                </div>
            )}

            <CommandList>
                {status === 'idle' && <Message>Введіть Wi-Fi, напр. *12345</Message>}
                {status === 'invalid' && <Message>Формат: * і 4–6 цифр</Message>}
                {status === 'loading' && <Message>Пошук…</Message>}
                {status === 'error' && <Message>Помилка пошуку. Спробуйте ще раз.</Message>}
                {status === 'ready' && items.length === 0 && <Message>{`Ключів для ${wifi} не знайдено`}</Message>}

                {status === 'ready' && items.length > 0 && (
                    <div className="px-2 py-1">
                        {items.map((k) => (
                            <CommandItem key={k.id} value={String(k.id)} onSelect={() => toggle(k.id)} className="items-start gap-3 py-2">
                                <Checkbox checked={selectedIds.has(k.id)} tabIndex={-1} aria-hidden className="pointer-events-none mt-0.5" />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-baseline gap-3">
                                        <span className="font-mono text-xs">
                                            <span className="mr-1 text-[10px] text-muted-foreground">RX</span>
                                            {k.rx_key}
                                        </span>
                                        <span className="font-mono text-xs">
                                            <span className="mr-1 text-[10px] text-muted-foreground">TX</span>
                                            {k.tx_key ?? '—'}
                                        </span>
                                        <span className="min-w-0 flex-1 truncate text-xs" title={k.comment ?? undefined}>
                                            {k.comment || '—'}
                                        </span>
                                    </div>
                                    <div className="mt-0.5 flex items-center gap-3 text-[11px] text-muted-foreground">
                                        {k.owner ? (
                                            <a
                                                href={k.owner.url}
                                                onPointerDown={(e) => e.stopPropagation()}
                                                onClick={(e) => openOwner(e, k.owner!.url)}
                                                className="truncate underline-offset-2 hover:text-foreground hover:underline"
                                            >
                                                {k.owner.name}
                                            </a>
                                        ) : (
                                            <span>—</span>
                                        )}
                                        <span>{formatDateTime(k.created_at)}</span>
                                    </div>
                                </div>
                            </CommandItem>
                        ))}
                    </div>
                )}

                {status === 'ready' && truncated && <div className="px-4 pb-3 text-xs text-muted-foreground">Показано перші 100</div>}
            </CommandList>

            <div className="flex items-center gap-4 border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
                <Hint keys={['↵']} label="вибрати" />
                <Hint keys={['⌘', '↵']} label="копіювати CSV" />
                <Hint keys={['esc']} label="назад" />
            </div>
        </>
    );
}
